import { z } from 'zod';
import { ServerId } from './basics.js';
import { ComplianceScope } from './compliance.js';
import { COMPLIANCE_EXCEPTION_LIMITS, CheckException, ComplianceExceptionStatus, EXCEPTION_NEEDS_EXPIRY, } from './compliance-posture.js';
import { ScaPolicyId } from './wazuh-findings.js';
/**
 * Recording a decision about a failing check (P11.20): `POST /v1/compliance/
 * exceptions` names the check and the servers it applies to (one, a group's, or
 * all), a status, and a reason that a reader six months from now can understand.
 * An admin's act, audited, and one row per server so each can end on its own.
 */
export const CreateComplianceException = z
    .object({
    policyId: ScaPolicyId,
    checkId: z.number().int().nonnegative(),
    status: ComplianceExceptionStatus,
    reason: z
        .string()
        .trim()
        .min(COMPLIANCE_EXCEPTION_LIMITS.reasonMin, 'Say why, in a sentence a colleague could follow')
        .max(COMPLIANCE_EXCEPTION_LIMITS.reasonMax),
    /** Required to carry a risk or promise a fix; at most a year ahead. */
    expiresAt: z.iso.datetime().optional(),
    /** Which servers: one, a group's, or (neither) every server where the check fails. */
    scope: ComplianceScope.default({}),
})
    .strict()
    .refine((body) => !EXCEPTION_NEEDS_EXPIRY.includes(body.status) || body.expiresAt !== undefined, {
    message: 'An accepted risk or a planned fix needs an end date',
    path: ['expiresAt'],
});
export const CreateComplianceExceptionResult = z.object({
    /** Servers the exception now covers. */
    created: z.number().int().nonnegative(),
    /** Servers where the check does not fail, or already had an active exception. */
    skipped: z.number().int().nonnegative(),
});
/** One row of the exceptions list. */
export const ComplianceException = CheckException.extend({
    serverId: ServerId,
    serverName: z.string().max(200),
    policyId: ScaPolicyId,
    checkId: z.number().int().nonnegative(),
    title: z.string().max(300),
    createdAt: z.iso.datetime(),
});
export const ComplianceExceptionList = z.object({
    items: z.array(ComplianceException).max(1000),
    /** More than the list shows. */
    total: z.number().int().nonnegative(),
});
export const ComplianceExceptionQuery = ComplianceScope.extend({
    /** Only these; by default all, with the expired ones last. */
    status: ComplianceExceptionStatus.optional(),
});
//# sourceMappingURL=compliance-exceptions.js.map